import toast from "react-hot-toast";
import {
  AppleIcon,
  EmailIcon,
  GoogleIcon,
  LogIcon,
  NotificationIcon,
  UserCrossIcon,
  UserIcon,
} from "../../assets";
import {
  HiddenBlock,
  IconButton,
  UserBadge,
  UserColorBadge,
} from "../../components";
import { IUser } from "../../models";
import {
  generateUserLogs,
  muteUser,
  notifyUser,
} from "../../services/users.service";
import {
  capitalizeFirstLetter,
  formatNumberToLocaleString,
} from "../../utils/methods";
import styles from "./styles.module.scss";
import dayjs from "dayjs";
import { useState } from "react";
import { MuteUser } from "../../modals";

type UserInfoProps = {
  user: IUser;
  refetchUser: () => void;
  refetchUsers: () => void;
};

const UserInfoCoachView: React.FC<UserInfoProps> = ({
  user,
  refetchUser,
  refetchUsers,
}) => {
  const age = dayjs().diff(dayjs(user?.dateOfBirth), "year");

  const [isLoadingNotify, setIsLoadingNotify] = useState(false);
  const [isLoadingGenerateLogs, setIsLoadingGenerateLogs] = useState(false);
  const [isShowMuteModal, setIsShowMuteModal] = useState(false);
  const [isLoadingMute, setIsLoadingMute] = useState<boolean>(false);

  const onUnmuteUser = async () => {
    try {
      setIsLoadingMute(true);
      await muteUser(user?.id || 0);
      refetchUsers();
      setIsLoadingMute(false);
    } catch (error) {
      toast.error(error?.message?.[0]?.message || "Something went wrong");
      setIsLoadingMute(false);
    }
  };

  const onGenerateUserLogs = async (id: number) => {
    try {
      setIsLoadingGenerateLogs(true);

      const { data } = await generateUserLogs(id);
      if (data?.message === "Ok") {
        toast.success("Logs were successfully generated");
      }
    } catch (error) {
      toast.error(error?.message?.[0]?.message || "Something went wrong");
    } finally {
      setIsLoadingGenerateLogs(false);
    }
  };

  const onNotifyUser = async (id: number) => {
    try {
      setIsLoadingNotify(true);
      const { data } = await notifyUser(id);
      if (data?.success) {
        toast.success("User was successfully notified");
      }
    } catch (error) {
      toast.error(error?.message?.[0]?.message || "Something went wrong");
    } finally {
      setIsLoadingNotify(false);
    }
  };

  function openEmail(email: string, subject = "", body = "") {
    const mailtoUrl = `mailto:${email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;

    const hadFocus = document.hasFocus();

    window.location.href = mailtoUrl;

    setTimeout(() => {
      const hasFocus = document.hasFocus();

      if (hadFocus && hasFocus) {
        const gmailUrl = `https://mail.google.com/mail/?view=cm&to=${email}&su=${encodeURIComponent(
          subject
        )}&body=${encodeURIComponent(body)}`;
        window.open(gmailUrl, "_blank");
      }
    }, 1000);
  }

  const onEmailUser = async (id: number) => {
    openEmail(user?.email);
  };

  return (
    <div className={styles.wrapper}>
      <MuteUser
        isOpen={isShowMuteModal}
        setIsOpen={setIsShowMuteModal}
        userId={user?.id || 0}
        onGetUserInfo={() => {
          refetchUser();
        }}
        refetchUsers={refetchUsers}
      />

      <div className={styles.headerContainer}>
        <div className={styles.userAvatar}>
          {user?.image ? (
            <img src={user.image} className={styles.avatar} alt="avatar" />
          ) : (
            <UserIcon />
          )}
        </div>
        <div className={styles.container}>
          <div className={styles.badgeContainer}>
            <UserBadge
              color={user?.color}
              isProd={!!user?.isProd}
              size="large"
            />
            <UserColorBadge color={user?.color} />
          </div>
          <p className={styles.userName}>
            {user?.firstName} {user?.lastName} <span>({age})</span>
          </p>
          <div className={styles.emailContainer}>
            {user?.authData?.registrationType === "google" ? (
              <GoogleIcon />
            ) : user?.authData?.registrationType === "apple" ? (
              <AppleIcon />
            ) : null}
            <p className={styles.body1Title}>{user?.email}</p>
          </div>

          <p className={styles.body1Title}>
            Subscription plan:{" "}
            {user?.subscriptionStatus
              ? `${capitalizeFirstLetter(user?.subscriptionStatus || "")} - `
              : ""}
            {user?.subscriptionType?.includes("monthly")
              ? "1 Month"
              : user?.subscriptionType?.includes("3months")
              ? "3 Months"
              : user?.subscriptionType?.includes("6months")
              ? "6 Months"
              : user?.subscriptionType?.includes("annual")
              ? "Annual"
              : user?.subscriptionType?.includes("onetimepayment")
              ? "Lifetime"
              : "-"}
          </p>
          <p className={styles.body1Title}>
            Referral: {user?.referral ? user?.referral : "-"}
          </p>
          {user?.cancellationDate ? (
            <p className={styles.cancellationDate}>
              Canceled:{dayjs(user?.cancellationDate).format("DD MMM, YYYY")}
            </p>
          ) : null}
        </div>
      </div>

      <div className={styles.infoContainerContainer}>
        <div className={styles.infoBlock}>
          <p className={styles.body1Title}>Registered:</p>
          <p className={styles.title16}>
            {dayjs(user?.createdAt).format("DD MMM, YYYY")}
          </p>
        </div>
        <div className={styles.infoBlock}>
          <p className={styles.body1Title}>Last completed day:</p>
          <p className={styles.title16}>
            {user?.last?.lastCompletedDay
              ? dayjs(user?.last?.lastCompletedDay).format("DD MMM, YYYY")
              : "-"}
          </p>
        </div>

        <div className={styles.infoBlock}>
          <p className={styles.body1Title}>Total completed days:</p>
          <p className={styles.title16}>
            <span>{user?.numberOfCompletedDays}</span>{" "}
            <span
              style={{
                fontWeight: 600,
                fontSize: 14,
                color: "#808191",
              }}
            >
              / {user?.numberOfDays}
            </span>
          </p>
        </div>
        <HiddenBlock
          title="Date of Birth"
          value={dayjs(user?.dateOfBirth).format("DD MMM, YYYY")}
          width={180}
        />
        <div style={{ width: "84px" }} className={styles.infoBlock}>
          <p className={styles.body1Title}>Height:</p>
          <p className={styles.title16}>
            {user?.height?.unit === "cm"
              ? `${formatNumberToLocaleString(user?.height?.cm || 0, 1)} cm`
              : `${formatNumberToLocaleString(
                  user?.height?.feet || 0,
                  1
                )} ft ${formatNumberToLocaleString(
                  user?.height?.inches || 0,
                  1
                )} in`}
          </p>
        </div>
        <HiddenBlock title="Sex" value={user?.gender} width={84} />
        <HiddenBlock title="Phone" value={user?.phone || "Empty"} width={180} />
      </div>
      <div className={styles.actionButtonsContainer}>
        <IconButton
          title="Notify"
          icon={<NotificationIcon />}
          isLoading={isLoadingNotify}
          onClick={() => onNotifyUser(user?.id || 0)}
        />
        <IconButton
          icon={<EmailIcon />}
          title="Email"
          onClick={() => onEmailUser(user?.id || 0)}
        />
        <IconButton
          icon={<UserCrossIcon />}
          title={user?.color === "9_grey" ? "Unignore" : "Ignore"}
          onClick={() => {
            if (user?.color === "9_grey") {
              onUnmuteUser();
            } else {
              setIsShowMuteModal(true);
            }
          }}
          isLoading={isLoadingMute}
        />
        <IconButton
          icon={<LogIcon />}
          title="Logs"
          isLoading={isLoadingGenerateLogs}
          onClick={() => onGenerateUserLogs(user?.id || 0)}
        />
      </div>
    </div>
  );
};

export default UserInfoCoachView;
